<template>
  <div class="w-full">
    <!-- Input Field -->
    <div class="relative mb-8" :class="{ 'md:w-1/2 md:mx-auto mb-6': props.centeredInput }">
      <!-- Activities Label -->
      <div class="absolute -top-3 right-3 z-10">
        <span class="bg-[#e0f7ef] text-[#4ECF84] text-xs font-medium px-3 py-1 rounded-[20px] border border-gray-200">Add Activities</span>
      </div>
      <input
        ref="inputRef"
        v-model="inputValue"
        type="text"
        placeholder="e.g. Diving, snorkeling"
        role="combobox"
        aria-label="Add activities"
        aria-haspopup="listbox"
        aria-controls="activity-listbox"
        :aria-expanded="showDropdown && filteredSuggestions.length > 0 && !isAtMaxLimit"
        :aria-activedescendant="highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined"
        :class="[
          props.desktopStyle
            ? 'w-full h-11 px-4 pr-12 rounded-full shadow-sm bg-white outline-none transition-all text-base border border-[#2F3033]'
            : 'w-full h-11 px-4 rounded-full border border-[#2F3033] focus:outline-none shadow-sm transition-all text-lg placeholder:text-lg',
          !props.desktopStyle && isMobile && 'focus:ring-2 focus:ring-[#4ECF84]/40 focus:border-[#4ECF84]',
          isAtMaxLimit && 'bg-gray-50 text-gray-400 cursor-not-allowed'
        ]"
        :disabled="isAtMaxLimit"
        @input="handleInput"
        @keydown="handleKeydown"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      
      <!-- Dropdown -->
      <Teleport to="body" :disabled="isMobile">
        <transition name="dropdown">
          <div
            v-if="showDropdown && filteredSuggestions.length > 0 && !isAtMaxLimit"
            ref="dropdownRef"
            :class="[
              'z-[9999] bg-white border border-gray-200 rounded-3xl md:rounded-lg shadow-lg overflow-y-auto',
              isMobile ? 'absolute mt-1 left-0 right-0' : 'fixed'
            ]"
            :style="isMobile ? { maxHeight: '240px' } : {
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: '240px'
            }"
            id="activity-listbox"
            role="listbox"
            aria-label="Activity suggestions"
          >
            <div
              v-for="(suggestion, index) in filteredSuggestions"
              :key="`${suggestion.category}-${suggestion.activity}`"
              :id="`option-${index}`"
              class="px-3 py-2 cursor-pointer transition-colors duration-150 ease-out"
              :class="[
                index === highlightedIndex 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'text-gray-700 hover:bg-gray-50'
              ]"
              @mousedown.prevent="selectSuggestion(suggestion)"
              @mouseenter="highlightedIndex = index"
              role="option"
              :aria-selected="index === highlightedIndex"
            >
              <div class="flex items-center">
                <component :is="getCategoryIcon(suggestion.category)" class="w-4 h-4 mr-2 text-gray-600" />
                <div>
                  <div class="font-normal">{{ suggestion.activity }}</div>
                  <div class="text-xs text-gray-500">{{ suggestion.category }}</div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </Teleport>
    </div>

    <!-- Selected Tags -->
    <div v-if="selectedTags.length > 0" class="mt-3 flex flex-wrap gap-2">
      <div
        v-for="tag in selectedTags"
        :key="getTagKey(tag)"
        class="inline-flex items-center px-3 py-1 rounded-3xl md:rounded-full text-sm font-medium transition-all duration-200 ease-out"
        :class="getTagClasses(tag)"
      >
        <component v-if="!tag.isCustom" :is="getCategoryIcon(tag.category)" class="w-4 h-4 mr-2" />
        <span v-if="tag.isCustom" class="text-sm mr-1 opacity-75">+</span>
        <span :class="{ 'italic': tag.isCustom }">{{ getTagDisplayText(tag) }}</span>
        <button
          @click="removeTag(tag)"
          :class="getRemoveButtonClasses(tag)"
          aria-label="Remove tag"
          type="button"
        >
          ×
        </button>
      </div>
    </div>

    <!-- Warning Message -->
    <transition name="warning-fade">
      <p v-if="isAtMaxLimit" class="text-xs text-gray-500 mt-1 transition-opacity duration-300">
        You've reached the maximum of {{ maxTags }} keywords.
      </p>
    </transition>

    <!-- Clear All Button -->
    <div v-if="selectedTags.length > 0 && !props.hideClearAll" class="mt-2">
      <button
        @click="clearAllTags"
        class="text-sm text-gray-600 hover:text-gray-800 transition-colors focus:outline-none focus:underline"
        type="button"
      >
        Clear all
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import activitiesData from '@/data/activities.json'

// Lucide Vue Next imports for category icons
import {
  Building,
  Palette,
  Leaf,
  Mountain,
  Bus,
  ChefHat,
  Tent,
  Users,
  Heart,
  ShoppingBag,
  MapPin
} from 'lucide-vue-next'

// Props
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  maxTags: {
    type: Number,
    default: 15
  },
  allowCustom: {
    type: Boolean,
    default: true
  },
  desktopStyle: {
    type: Boolean,
    default: false
  },
  hideClearAll: {
    type: Boolean,
    default: false
  },
  centeredInput: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'update:tags', 'newKeywordEntered'])

// Refs
const inputRef = ref(null)
const dropdownRef = ref(null)
const inputValue = ref('')
const showDropdown = ref(false)
const highlightedIndex = ref(-1)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })

// Reactive data
const selectedTags = ref([...props.modelValue])
const allActivities = ref([])

// Category icons mapping using Lucide icons
const categoryIconComponents = {
  'Sites': Building,
  'Workshops': Palette,
  'Nature': Leaf,
  'Outdoors': Mountain,
  'Tours': Bus,
  'Food': ChefHat,
  'Festival': Tent,
  'Community': Users,
  'Wellness': Heart,
  'Shopping': ShoppingBag,
  'Landforms': MapPin
}

// Computed properties
const filteredSuggestions = computed(() => {
  if (!inputValue.value.trim()) return []
  
  const searchTerm = inputValue.value.toLowerCase().trim()
  const filtered = allActivities.value.filter(activity => {
    // Check if already selected
    const isSelected = selectedTags.value.some(tag => 
      getTagDisplayText(tag).toLowerCase() === activity.activity.toLowerCase()
    )
    if (isSelected) return false
    
    // Filter by search term
    return activity.activity.toLowerCase().includes(searchTerm) ||
           activity.category.toLowerCase().includes(searchTerm)
  })
  
  // Limit results to prevent overwhelming dropdown
  return filtered.slice(0, 10)
})

const canAddMoreTags = computed(() => {
  return selectedTags.value.length < props.maxTags
})

const isAtMaxLimit = computed(() => {
  return selectedTags.value.length >= props.maxTags
})

const canCreateCustomTag = computed(() => {
  if (!props.allowCustom || !inputValue.value.trim() || !canAddMoreTags.value) return false
  
  const searchTerm = inputValue.value.toLowerCase().trim()
  
  // Check if it's already selected
  const isDuplicate = selectedTags.value.some(tag => 
    getTagDisplayText(tag).toLowerCase() === searchTerm
  )
  
  // Check if it matches a predefined suggestion
  const matchesPredefined = allActivities.value.some(activity =>
    activity.activity.toLowerCase() === searchTerm
  )
  
  return !isDuplicate && !matchesPredefined
})

// Methods
const getCategoryIcon = (category) => {
  return categoryIconComponents[category] || MapPin
}

const handleInput = () => {
  highlightedIndex.value = -1
  if (inputValue.value.trim() && !isAtMaxLimit.value) {
    showDropdown.value = true
    if (isMobile.value) {
      inputRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }
}

const handleFocus = () => {
  if (!isAtMaxLimit.value) {
    showDropdown.value = true
    if (isMobile.value) {
      setTimeout(() => {
        inputRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }
  }
}

const handleKeydown = (event) => {
  switch (event.key) {
    case 'ArrowDown':
      if (showDropdown.value && filteredSuggestions.value.length > 0) {
        event.preventDefault()
        highlightedIndex.value = Math.min(
          highlightedIndex.value + 1,
          filteredSuggestions.value.length - 1
        )
      }
      break
    case 'ArrowUp':
      if (showDropdown.value && filteredSuggestions.value.length > 0) {
        event.preventDefault()
        highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1)
      }
      break
    case 'Enter':
      event.preventDefault()
      if (showDropdown.value && highlightedIndex.value >= 0 && filteredSuggestions.value.length > 0) {
        // Select highlighted suggestion
        selectSuggestion(filteredSuggestions.value[highlightedIndex.value])
      } else if (canCreateCustomTag.value) {
        // Create custom tag
        createCustomTag()
      }
      break
    case ',':
      event.preventDefault()
      if (canCreateCustomTag.value) {
        createCustomTag()
      }
      break
    case 'Backspace':
      if (!inputValue.value && selectedTags.value.length > 0) {
        // Remove last tag if input is empty
        event.preventDefault()
        removeTag(selectedTags.value[selectedTags.value.length - 1])
      }
      break
    case 'Escape':
      event.preventDefault()
      showDropdown.value = false
      highlightedIndex.value = -1
      inputRef.value?.blur()
      break
  }
}

const handleBlur = () => {
  // Auto-confirm any typed text as a custom tag when leaving the input
  // (covers the case where the user types an activity and clicks Search without pressing Enter)
  if (canCreateCustomTag.value) {
    createCustomTag()
  }
  // Delay hiding dropdown to allow for click events on dropdown items
  setTimeout(() => {
    showDropdown.value = false
    highlightedIndex.value = -1
  }, 150)
}

const selectSuggestion = (suggestion) => {
  if (!canAddMoreTags.value) {
    return
  }

  // Check for duplicates
  const isDuplicate = selectedTags.value.some(tag => 
    getTagDisplayText(tag).toLowerCase() === suggestion.activity.toLowerCase()
  )
  
  if (!isDuplicate) {
    const newTag = {
      ...suggestion,
      isCustom: false
    }
    selectedTags.value.push(newTag)
    emitUpdate()
  }
  
  inputValue.value = ''
  showDropdown.value = false
  highlightedIndex.value = -1
  
  // Focus back on input
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const createCustomTag = () => {
  if (!canCreateCustomTag.value) return
  
  const customKeyword = inputValue.value.trim()
  const newTag = {
    activity: customKeyword,
    category: 'Custom',
    isCustom: true
  }
  
  selectedTags.value.push(newTag)
  emit('newKeywordEntered', customKeyword)
  emitUpdate()
  
  inputValue.value = ''
  showDropdown.value = false
  highlightedIndex.value = -1
  
  // Focus back on input
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const removeTag = (tagToRemove) => {
  selectedTags.value = selectedTags.value.filter(tag => 
    !(getTagDisplayText(tag) === getTagDisplayText(tagToRemove) && tag.isCustom === tagToRemove.isCustom)
  )
  emitUpdate()
}

const clearAllTags = () => {
  selectedTags.value = []
  emitUpdate()
  inputValue.value = ''
  nextTick(() => {
    inputRef.value?.focus()
  })
}

const emitUpdate = () => {
  emit('update:modelValue', selectedTags.value)
  emit('update:tags', selectedTags.value)
}

// Helper methods for tag display and styling
const getTagKey = (tag) => {
  return tag.isCustom ? `custom-${tag.activity}` : `${tag.category}-${tag.activity}`
}

const getTagDisplayText = (tag) => {
  return tag.activity
}

const getTagClasses = (tag) => {
  if (tag.isCustom) {
    return [
      'bg-blue-50 text-blue-700 border border-blue-200',
      'hover:bg-blue-100 cursor-default'
    ]
  } else {
    return [
      'bg-gray-100 text-gray-700',
      'hover:bg-gray-200 cursor-default'
    ]
  }
}

const getRemoveButtonClasses = (tag) => {
  if (tag.isCustom) {
    return 'ml-2 text-blue-600 hover:text-blue-800 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-blue-50 rounded'
  } else {
    return 'ml-2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 focus:ring-offset-gray-100 rounded'
  }
}

// Initialize activities data
const initializeActivities = () => {
  allActivities.value = []
  
  Object.entries(activitiesData.categories).forEach(([category, activities]) => {
    activities.forEach(activity => {
      allActivities.value.push({
        category,
        activity
      })
    })
  })
  
  // Sort alphabetically by activity name
  allActivities.value.sort((a, b) => a.activity.localeCompare(b.activity))
}

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  selectedTags.value = [...newValue]
}, { deep: true })

const isMobile = ref(false)
const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 768
}

// Lifecycle
onMounted(() => {
  initializeActivities()
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize)
})

const updateDropdownPosition = () => {
  if (isMobile.value) return // Mobile uses relative positioning
  if (inputRef.value) {
    const rect = inputRef.value.getBoundingClientRect()
    dropdownPosition.value = {
      top: rect.bottom + 4, // 4px gap
      left: rect.left,
      width: rect.width
    }
  }
}

// Watch for dropdown visibility to handle positioning
watch(showDropdown, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition()
      window.addEventListener('resize', updateDropdownPosition)
      window.addEventListener('scroll', updateDropdownPosition, true)
    })
  } else {
    window.removeEventListener('resize', updateDropdownPosition)
    window.removeEventListener('scroll', updateDropdownPosition, true)
  }
})
</script>

<style scoped>
/* Dropdown animations */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-out;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Warning message fade animation */
.warning-fade-enter-active,
.warning-fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.warning-fade-enter-from,
.warning-fade-leave-to {
  opacity: 0;
}

.warning-fade-enter-to,
.warning-fade-leave-from {
  opacity: 1;
}

/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Focus styles - only on mobile to prevent desktop halo */
@media (max-width: 767px) {
  input:focus {
    box-shadow: 0 0 0 3px rgba(78, 207, 132, 0.15); /* Macumba green halo for mobile */
  }
}

input:focus {
  outline: none;
}

/* Tag hover effects */
.inline-flex:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Mobile responsive adjustments */
@media (max-width: 640px) {
  .max-h-60 {
    max-height: 12rem;
  }
  
  .px-3 {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  
  .py-2 {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  

}

/* Ensure proper stacking */
.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

/* Smooth transitions for all interactive elements */
button,
.cursor-pointer {
  transition: all 0.15s ease-in-out;
}

/* Enhanced focus visibility - only on mobile */
@media (max-width: 767px) {
  button:focus-visible,
  input:focus-visible {
    outline: 2px solid #4ECF84;
    outline-offset: 2px;
  }
}

/* Remove default focus outline on desktop */
@media (min-width: 768px) {
  button:focus-visible,
  input:focus-visible {
    outline: none;
  }
}
</style>
